/**
 * Per-user local state isolation.
 *
 * Several pieces of UI state (chat history, chat panel open flag) live in
 * plain localStorage. Because localStorage is
 * device-scoped, signing in as a different user inherits the previous
 * user's state. We detect user-id transitions and purge the affected keys
 * on change so each user starts with a clean slate.
 */

const LAST_USER_KEY = 'danny_last_user_id';

const PER_USER_KEYS = [
  'danny_chat_history',
  'danny_chat_panel_open',
  'danny_first_chat_seen', // legacy; removed on load but purge defensively
  'danny_first_message_dismissed',
];

/**
 * Called after resolving the current user. If the current user id differs
 * from the one we saw last, clear per-user keys and record the new id.
 *
 * Returns true if a purge happened (caller can reload to re-init contexts
 * that already read stale values).
 */
export function syncPerUserLocalState(userId: string | null): boolean {
  try {
    const last = localStorage.getItem(LAST_USER_KEY);
    if (userId === null) {
      // Signed out — remember nothing; don't purge here because the user
      // might sign back in as the same account.
      return false;
    }
    if (last === userId) return false;

    // User changed (or first sign-in on this device). Purge.
    for (const key of PER_USER_KEYS) {
      localStorage.removeItem(key);
    }
    localStorage.setItem(LAST_USER_KEY, userId);
    return last !== null; // only return "purged" when we overrode someone else's state
  } catch {
    return false;
  }
}
